/**
 * Upcoming Events Card Component
 *
 * Shows upcoming mosque events and campaigns fetched from /api/v1/community/{companyId}/events
 */

import { FC } from 'react';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useUpcomingEvents } from '@/hooks/useCommunity';
import type { EventType } from '@/types/community.types';

const typeColors: Record<EventType, string> = {
  fundraiser: 'bg-green-100 text-green-800',
  event: 'bg-blue-100 text-blue-800',
  campaign: 'bg-purple-100 text-purple-800',
};

export const UpcomingEventsCard: FC = () => {
  const { data, isLoading, error, refetch } = useUpcomingEvents();

  // Loading skeleton
  if (isLoading && !data) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/2 mb-1"></div>
          <div className="h-4 bg-gray-100 rounded w-2/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
        <div className="text-center py-4">
          <p className="text-red-600 text-sm font-medium">Failed to load events</p>
          <p className="text-gray-500 text-xs mt-1">{error.message}</p>
          <button
            onClick={refetch}
            disabled={isLoading}
            className="mt-4 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const events = data?.events ?? [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
          <p className="text-sm text-gray-600">Community activities & campaigns</p>
        </div>
        <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          View Calendar →
        </button>
      </div>

      <div className="space-y-3">
        {events.map((event) => (
          <div
            key={event.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold text-gray-900">{event.title}</h4>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[event.type]}`}
                  >
                    {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600 gap-4">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {new Date(event.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}{' '}
                    • {event.time}
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {event.location}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>No upcoming events</p>
        </div>
      )}
    </div>
  );
};
